/**
 * Сессия как состояние приложения: кто вошёл, в какой организации, что ему
 * можно.
 *
 * Здесь же живёт единственный ответ на вопрос «а что показывать». Он собран из
 * двух источников, и оба принадлежат серверу:
 *
 * * `session.permissions` — плоский список действий, посчитанный сервером;
 * * матрица `x-seeno-access` из контракта — «дотянется ли роль до такого пути».
 *
 * Фронт не выводит права из роли сам. Единственное, что он делает своей
 * головой, — прячет то, до чего роль не дотянется, чтобы человек не встречал
 * 403 кнопкой.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { onSessionLost } from './api/client';
import { ApiError } from './api/problem';
import { getSession, logout, switchContext, type Session } from './api/auth';
import { allows, granted, isReadOnly, type Role } from './api/access';

export type SessionState =
  | { kind: 'checking' }
  | { kind: 'anonymous' }
  | { kind: 'signed'; session: Session }
  | { kind: 'failed'; error: unknown };

interface SessionApi {
  state: SessionState;
  /** Перечитать сессию с сервера. Вызывается после входа и смены контекста. */
  reload: () => void;
  /** Положить сессию, полученную ответом на вход, — без лишнего запроса. */
  adopt: (session: Session) => void;
  signOut: () => void;
  switchOrganization: (organizationId: string) => Promise<void>;
}

const Context = createContext<SessionApi | null>(null);

/** Общий пустой список прав: см. пояснение у места использования. */
const NO_PERMISSIONS: never[] = [];

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ kind: 'checking' });
  // Счётчик попыток, а не вызов проверки прямо из эффекта: состояние внутри
  // эффекта ставится только из ответа, иначе получается каскад перерисовок.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const abort = new AbortController();
    getSession(abort.signal, true)
      .then((session) => setState({ kind: 'signed', session }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Нет куки или подпись не сошлась — это не сбой, а обычное состояние
        // человека, который ещё не вошёл.
        if (error instanceof ApiError && error.problem.status === 401) {
          setState({ kind: 'anonymous' });
          return;
        }
        setState({ kind: 'failed', error });
      });
    return () => abort.abort();
  }, [attempt]);

  const reload = useCallback(() => {
    setState({ kind: 'checking' });
    setAttempt((value) => value + 1);
  }, []);

  // Сессия истекла посреди работы: refresh не обменялся. Показываем вход, а не
  // ошибку экрана — иначе человек обновляет страницу, пока не догадается сам.
  useEffect(() => onSessionLost(() => setState({ kind: 'anonymous' })), []);

  const value = useMemo<SessionApi>(
    () => ({
      state,
      reload,
      adopt: (session: Session) => setState({ kind: 'signed', session }),
      signOut: () => {
        // Состояние меняем сразу: выход не должен ждать сети. Даже если запрос
        // не дойдёт, куки живут 15 минут, а человек уже видит форму входа.
        setState({ kind: 'anonymous' });
        void logout().catch(() => undefined);
      },
      switchOrganization: async (organizationId: string) => {
        const session = await switchContext(organizationId);
        setState({ kind: 'signed', session });
      },
    }),
    [state, reload],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

function useSessionApi(): SessionApi {
  const api = useContext(Context);
  if (!api) throw new Error('SessionProvider не найден выше по дереву');
  return api;
}

export function useSessionState(): SessionApi {
  return useSessionApi();
}

/** Сессия вошедшего. Зовётся только под оболочкой, где она заведомо есть. */
export function useSession(): Session {
  const { state } = useSessionApi();
  if (state.kind !== 'signed')
    throw new Error('Сессии нет: экран отрисован вне оболочки');
  return state.session;
}

export interface Access {
  role: Role | null;
  /** Роль ничего не меняет — экран работает в режиме чтения. */
  readOnly: boolean;
  /** Дотянется ли роль до пути контракта. Этим скрываются целые разделы. */
  reaches: (method: string, path: string) => boolean;
  /** Разрешено ли действие. Список считает сервер, фронт только спрашивает. */
  can: (action: string) => boolean;
}

/**
 * Права текущей сессии.
 *
 * `reaches` — про навигацию: раздел, до которого роль не дотянется, из меню
 * убирается целиком. `can` — про кнопку внутри экрана: там решает сервер,
 * потому что кроме роли на кнопку влияют тариф и состояние подписки.
 */
export function useAccess(): Access {
  const { state } = useSessionApi();
  const session = state.kind === 'signed' ? state.session : null;
  const role = (session?.membership?.role ?? null) as Role | null;
  // Общая пустая ссылка, а не свежий `[]`: иначе useMemo ниже пересчитывается
  // на каждой перерисовке и права «меняются» без всякой смены сессии.
  const permissions = session?.permissions ?? NO_PERMISSIONS;

  return useMemo(
    () => ({
      role,
      readOnly: isReadOnly(role),
      reaches: (method: string, path: string) => allows(role, method, path),
      can: (action: string) => granted(permissions, action),
    }),
    [role, permissions],
  );
}
